import { supabase } from '../lib/supabase';
import {
  Room,
  Network,
  PCInstance,
  Experience,
  ConfigurationRelease,
  Configuration,
  ExperienceRelease,
  PCSyncStatus,
  MultiplayerSessionDbStatus,
  RoomSoftwareTarget,
  PCSoftwareStatus,
  SoftwareComponent,
  OperatorDevice,
  OperatorPlatform,
  RoomStreamingSettings,
  PCStreamingSettings,
  StreamingSettings,
  ExperienceLaunchArgs,
  LaunchArgsScope,
  LaunchArgsValues,
  AgentConfig,
  AgentConfigScope,
  AgentConfigValues,
  HmdDevice,
  RoomPlayspace,
  PlanPoint,
  type OutOfServiceKind,
  type PCOutOfService,
} from '../types/database';

// ---- Diagnostics policy (operations.diagnostic_config) ----------------------
// The table is a global key/value store; per-room scoping lives INSIDE the jsonb
// value as { default, rooms: { [roomId]: T } }. enforcement + auto_run are
// room-scoped this way; disabled_tests + advisory_overrides are flat fleet-wide
// arrays. RLS is FOR ALL to authenticated, so the operator can write it directly.
export type DiagEnforcement = 'report_only' | 'block';

interface RoomScoped<T> {
  default: T;
  rooms: Record<string, T>;
}

export interface DiagnosticsPolicy {
  enforcement: RoomScoped<DiagEnforcement>;
  autoRun: RoomScoped<boolean>;
  disabledTests: string[]; // fleet-wide (not room-scoped)
  advisoryOverrides: string[]; // fleet-wide (not room-scoped)
}

// Per-PC aggregate for a test run (telemetry.test_run_report RPC).
export interface TestRunPcStat {
  pc_name: string;
  samples: number;
  net_sent_avg: number | null;
  net_sent_max: number | null;
  net_recv_avg: number | null;
  bitrate_avg: number | null;
  bitrate_min: number | null;
  transmit_avg: number | null;
  transmit_max: number | null;
  capture_avg: number | null;
  stream_fps_avg: number | null;
  render_fps_avg: number | null;
  render_fps_min: number | null;
  gpu_avg: number | null;
  gpu_max: number | null;
  gpu_temp_max: number | null;
  scenes: string | null;
}

// Per-scene aggregate for a test run (telemetry.test_run_scene_report RPC).
export interface TestRunSceneStat {
  scene_name: string | null;
  samples: number;
  pcs: number;
  net_sent_avg: number | null;
  bitrate_avg: number | null;
  transmit_avg: number | null;
  render_fps_avg: number | null;
  render_fps_min: number | null;
  gpu_avg: number | null;
}

// Per-version soak verdicts for a test run (telemetry.test_run_soak_report RPC).
// Computed from the agent-written experience_runs/experience_crashes rows inside
// the run's window; inconclusive cycles were invalidated by the orchestrator, so
// runs = verified cycles (completed + crashed).
export interface TestRunSoakStat {
  version: string;
  runs: number;
  completed: number;
  crashed: number;
  crash_total: number;
  avg_run_min: number | null;
  crash_scenes: string | null;
}

// One crash row inside a soak run's window (telemetry.test_run_soak_crashes RPC).
export interface TestRunSoakCrash {
  crashed_at: string;
  pc_name: string;
  version: string | null;
  scene_index: number;
  scene_name: string | null;
  exit_code: number;
}

// One labeled streaming test run (telemetry.test_runs) — see the Test Lab page.
export interface TestRun {
  id: string;
  created_at: string;
  label: string;
  config_notes: string | null;
  room_id: string | null;
  pc_names: string[];
  experience_id: string | null;
  experience_version: string | null;
  started_at: string;
  ended_at: string | null;
  started_by: string | null;
}

// Row shape for a direct operator-written incident marker (operations.incidents).
// `type` is always 'manual' — the feed taxonomy (freeze/crash) stays agent-owned.
export interface IncidentInsert {
  severity: string;
  source: string;
  message: string;
  pc_instance_id?: string | null;
  room_id?: string | null;
  experience_id?: string | null;
  run_id?: string | null;
  metadata?: Record<string, unknown>;
}

// One operations.file_drops row: a file parked in the private file-drop bucket
// for manual operator <-> PC / PC <-> PC transfer. 7-day retention (DB default
// expires_at + nightly cleanup cron).
export interface FileDrop {
  id: string;
  file_name: string;
  object_path: string | null;
  signed_url: string | null;
  size_bytes: number | null;
  content_type: string | null;
  note: string | null;
  source: 'agent' | 'operator';
  source_pc_instance_id: string | null;
  source_pc_name: string | null;
  room_id: string | null;
  status: 'uploading' | 'ready' | 'error';
  error: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  expires_at: string;
}

// Recent-rows window for getSceneNormals. A few hundred rows is ~40-80 plays
// per scene for a typical experience: enough for a stable median, small enough
// to pull on demand, and recent enough to describe the build now in the room.
const SCENE_NORMAL_ROWS = 500;
// Below this many rows the room's own history is too thin to judge by, so the
// fleet-wide median is used instead (and the UI says so).
const SCENE_NORMAL_MIN_ROOM_ROWS = 25;

/** What each scene of an experience normally takes, plus the scene names. */
export interface SceneNormals {
  /** level_order (0-based scene index) -> median duration of recent plays. */
  byLevel: Map<number, { medianMs: number; samples: number }>;
  /** experiences.experience_scenes.name by index (the friendly name). */
  nameByIndex: Map<number, string>;
  sceneCount: number | null;
  /** Whether the medians come from this room's runs or from the whole fleet. */
  scope: 'room' | 'fleet';
}

export class DatabaseService {
  // Room queries
  async getRoom(roomId: string): Promise<Room | null> {
    const { data, error } = await supabase
      .schema('infra')
      .from('rooms')
      .select('*')
      .eq('id', roomId)
      .single();

    if (error) {
      console.error('Error fetching room:', error);
      return null;
    }

    console.log('Fetched room data:', data);
    return data;
  }

  async listRooms(): Promise<Room[]> {
    const { data, error } = await supabase
      .schema('infra')
      .from('rooms')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching rooms:', error);
      return [];
    }

    return data || [];
  }

  // Network queries
  // Fleet-wide network list (id → room). Used to place headsets by the network
  // they announced on, even when the view is filtered to a single room.
  async listNetworks(): Promise<Network[]> {
    const { data, error } = await supabase.schema('infra').from('networks').select('*');
    if (error) {
      console.error('Error listing networks:', error);
      return [];
    }
    return (data || []) as Network[];
  }

  async getNetworksInRoom(roomId: string): Promise<Network[]> {
    const { data, error } = await supabase
      .schema('infra')
      .from('networks')
      .select('*')
      .eq('room_id', roomId)
      .order('name');

    if (error) {
      console.error('Error fetching networks:', error);
      return [];
    }

    return data || [];
  }

  // PC Instance queries
  //
  // Everything below returns only experience PCs. Room NUCs live in the same
  // table (so the agent's WOL waker finds them without a code change) but have no
  // agent, no heartbeat and nothing listening on :9090, so they must never reach a
  // tile, a software target, a test-lab run or a shutdown loop.
  // getWakeTargetsInRoom is the one deliberate exception.
  async getPCInstancesInNetwork(networkId: string): Promise<PCInstance[]> {
    const { data, error } = await supabase
      .schema('infra')
      .from('pc_instances')
      .select('*')
      .eq('network_id', networkId)
      .eq('device_kind', 'experience_pc')
      .order('player_index');

    if (error) {
      console.error('Error fetching PC instances:', error);
      return [];
    }

    return data || [];
  }

  // Every PC in the fleet (all rooms) — file-drop transfers are cross-room.
  async getAllPCInstances(): Promise<PCInstance[]> {
    const { data, error } = await supabase
      .schema('infra')
      .from('pc_instances')
      .select('*')
      .eq('device_kind', 'experience_pc')
      .order('pc_name');

    if (error) {
      console.error('Error fetching PC instances:', error);
      return [];
    }

    return data || [];
  }

  async getPCInstancesInRoom(roomId: string): Promise<PCInstance[]> {
    // First get all networks in the room
    const networks = await this.getNetworksInRoom(roomId);
    const networkIds = networks.map((n) => n.id);

    if (networkIds.length === 0) {
      return [];
    }

    // Then get all PC instances in those networks
    const { data, error } = await supabase
      .schema('infra')
      .from('pc_instances')
      .select('*')
      .in('network_id', networkIds)
      .eq('device_kind', 'experience_pc')
      .order('player_index');

    if (error) {
      console.error('Error fetching PC instances:', error);
      return [];
    }

    return data || [];
  }

  // Every machine in a room that can be woken over LAN, INCLUDING room NUCs.
  // Deliberately unfiltered on device_kind: the agent's waker only needs a MAC and
  // a network, so an agentless box is a perfectly valid target.
  //
  // Use this only for wake requests. Anything that talks to an agent — restart,
  // shutdown, status, launch — must use getPCInstancesInRoom instead.
  async getWakeTargetsInRoom(roomId: string): Promise<PCInstance[]> {
    const networks = await this.getNetworksInRoom(roomId);
    const networkIds = networks.map((n) => n.id);

    if (networkIds.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .schema('infra')
      .from('pc_instances')
      .select('*')
      .in('network_id', networkIds)
      .order('player_index');

    if (error) {
      console.error('Error fetching wake targets:', error);
      return [];
    }

    return data || [];
  }

  // Fetch a single PC instance row by id. Used by the tile to poll the agent's
  // lifecycle intent (status / diag_state / last_heartbeat) while it is
  // unreachable — the /api/status poll is dead then, and the room-level prop only
  // refreshes on a manual refresh.
  async getPCInstanceById(id: string): Promise<PCInstance | null> {
    const { data, error } = await supabase
      .schema('infra')
      .from('pc_instances')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching PC instance:', error);
      return null;
    }

    return data;
  }

  // Configuration queries
  async getConfiguration(configId: string): Promise<Configuration | null> {
    const { data, error } = await supabase
      .from('configurations')
      .select('*')
      .eq('id', configId)
      .single();

    if (error) {
      console.error('Error fetching configuration:', error);
      return null;
    }

    return data;
  }

  async getConfigurationReleases(
    configId: string,
    phase: string = 'production',
    includeAllPhases: boolean = false
  ): Promise<ConfigurationRelease[]> {
    console.log('Fetching configuration releases for config:', configId, 'phase:', phase, 'includeAllPhases:', includeAllPhases);

    // configuration_release (singular) is in the monitor schema
    let query = supabase
      .schema('monitor')
      .from('configuration_release')
      .select('*')
      .eq('configuration_id', configId);

    // If includeAllPhases is true (for admins), fetch production, beta, and alpha
    if (includeAllPhases) {
      query = query.in('phase', ['production', 'beta', 'alpha']);
    } else {
      query = query.eq('phase', phase);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching configuration releases:', error);
      return [];
    }

    console.log('Raw configuration_release data:', data);

    // Fetch experiences and experience_releases separately and merge
    const releases = data || [];
    const experienceIds = [...new Set(releases.map((r: any) => r.experience_id))];
    const experienceReleaseIds = [...new Set(releases.map((r: any) => r.experience_release_id).filter(Boolean))];

    console.log('Experience IDs to fetch:', experienceIds);
    console.log('Experience Release IDs to fetch:', experienceReleaseIds);

    // Fetch experiences (in experiences schema)
    let expMap = new Map();
    if (experienceIds.length > 0) {
      const { data: experiences, error: expError } = await supabase
        .schema('experiences')
        .from('experiences')
        .select('*')
        .in('id', experienceIds);

      console.log('Fetched experiences:', experiences);

      if (!expError && experiences) {
        expMap = new Map(experiences.map((e: any) => [e.id, e]));
      }
    }

    // Fetch language support for experiences using RPC function
    let languageSupportMap = new Map<string, any[]>();
    if (experienceIds.length > 0) {
      // Call the database function for each experience
      const languagePromises = experienceIds.map(async (expId) => {
        const { data, error } = await supabase
          .schema('experiences')
          .rpc('get_experience_languages_by_uuid', {
            p_experience_id: expId
          });

        if (!error && data) {
          return { expId, languages: data.map((lang: any) => ({
            id: lang.language_id?.toString() || '',
            experience_id: expId,
            elevenlabs_agent_id: lang.elevenlabs_agent_id,
            priority: lang.priority,
            language: {
              id: lang.language_id,
              name: lang.language_name,
              short_name: lang.language_short_name,
              created_at: ''
            }
          }))};
        }
        return { expId, languages: [] };
      });

      const languageResults = await Promise.all(languagePromises);
      languageResults.forEach(({ expId, languages }) => {
        languageSupportMap.set(expId, languages);
      });

      console.log('Fetched language support via RPC:', languageSupportMap);
    }

    // Merge language support into experiences
    expMap.forEach((exp, expId) => {
      exp.supported_languages = languageSupportMap.get(expId) || [];
    });

    // Fetch experience_releases (in monitor schema)
    let expReleaseMap = new Map();
    if (experienceReleaseIds.length > 0) {
      const { data: experienceReleases, error: expRelError } = await supabase
        .schema('monitor')
        .from('experience_releases')
        .select('*')
        .in('id', experienceReleaseIds);

      console.log('Fetched experience_releases:', experienceReleases);

      if (!expRelError && experienceReleases) {
        expReleaseMap = new Map(experienceReleases.map((er: any) => [er.id, er]));
      }
    }

    // Merge everything together
    const mergedReleases = releases.map((release: any) => ({
      ...release,
      experiences: expMap.get(release.experience_id),
      experience_release: expReleaseMap.get(release.experience_release_id)
    }));

    console.log('Returning merged releases:', mergedReleases);
    return mergedReleases;
  }

  // Experience queries
  async listExperiences(): Promise<Experience[]> {
    const { data, error } = await supabase
      .schema('experiences')
      .from('experiences')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching experiences:', error);
      return [];
    }

    return data || [];
  }

  async getExperience(experienceId: string): Promise<Experience | null> {
    const { data, error } = await supabase
      .schema('experiences')
      .from('experiences')
      .select('*')
      .eq('id', experienceId)
      .single();

    if (error) {
      console.error('Error fetching experience:', error);
      return null;
    }

    return data;
  }

  // What a scene normally takes in this room, for the playspace map's
  // "4:12 in scene (normaal 3:16)" line.
  //
  // experiences.level_times holds one row per finished scene play (written by
  // the agent's RecordLevelTime), so the typical duration is just a quantile
  // over recent rows. The MEDIAN, not the mean: a visitor who put the headset
  // down mid-scene leaves a 40-minute row that would drag an average up and
  // make every later comparison useless.
  //
  // Read client-side rather than through an RPC: the table has no RLS and anon
  // already has select, so this needs no migration. Only the most recent rows
  // are pulled (a few hundred), which is both small and the freshest picture of
  // the current build. Rooms differ (experiences.room_level_rules can skip
  // scenes, and audiences differ), so this room's own rows are preferred and
  // the fleet is the fallback when the room has too few.
  async getSceneNormals(experienceId: string, roomId: string | null): Promise<SceneNormals> {
    const out: SceneNormals = { byLevel: new Map(), nameByIndex: new Map(), sceneCount: null, scope: 'fleet' };

    const scenes = await supabase
      .schema('experiences')
      .from('experience_scenes')
      .select('index, name')
      .eq('experience_id', experienceId)
      .order('index');
    if (scenes.error) console.error('Error fetching experience scenes:', scenes.error);
    if (scenes.data) {
      out.sceneCount = scenes.data.length || null;
      for (const s of scenes.data) out.nameByIndex.set(s.index, s.name);
    }

    // duration_ms bounds drop the two kinds of row that are not a visitor
    // playing: a zero-length blip and a scene left running for hours.
    const fetchTimes = async (scoped: boolean) => {
      let q = supabase
        .schema('experiences')
        .from('level_times')
        .select(scoped ? 'level_order, duration_ms, experience_runs!inner(room_id)' : 'level_order, duration_ms')
        .eq('experience_id', experienceId)
        .gt('duration_ms', 1000)
        .lt('duration_ms', 3600000)
        .order('created_at', { ascending: false })
        .limit(SCENE_NORMAL_ROWS);
      if (scoped && roomId) q = q.eq('experience_runs.room_id', roomId);
      const { data, error } = await q;
      if (error) {
        console.error('Error fetching level times:', error);
        return [];
      }
      return (data || []) as unknown as Array<{ level_order: number; duration_ms: number }>;
    };

    let rows = roomId ? await fetchTimes(true) : [];
    let scope: SceneNormals['scope'] = 'room';
    if (rows.length < SCENE_NORMAL_MIN_ROOM_ROWS) {
      rows = await fetchTimes(false);
      scope = 'fleet';
    }
    out.scope = scope;

    const byLevel = new Map<number, number[]>();
    for (const r of rows) {
      if (typeof r.level_order !== 'number' || typeof r.duration_ms !== 'number') continue;
      const list = byLevel.get(r.level_order);
      if (list) list.push(r.duration_ms);
      else byLevel.set(r.level_order, [r.duration_ms]);
    }
    byLevel.forEach((list, level) => {
      list.sort((a, b) => a - b);
      const mid = Math.floor(list.length / 2);
      const median = list.length % 2 ? list[mid] : Math.round((list[mid - 1] + list[mid]) / 2);
      out.byLevel.set(level, { medianMs: median, samples: list.length });
    });
    return out;
  }

  async getAvgRuntimeSeconds(experienceId: string): Promise<number | null> {
    const { data, error } = await supabase
      .schema('experiences')
      .rpc('get_avg_runtime_seconds_by_experience', {
        p_experience_id: experienceId
      });

    if (error) {
      console.error('Error fetching average runtime:', error);
      return null;
    }

    return data;
  }

  // PC Sync Status queries
  async getPCInstalledExperiences(
    pcInstanceId: string,
    configurationReleases: ConfigurationRelease[]
  ): Promise<ConfigurationRelease[]> {
    // Get all sync statuses for this PC
    const { data, error } = await supabase
      .schema('infra')
      .from('pc_sync_status')
      .select('configuration_release_id, sync_status')
      .eq('pc_instance_id', pcInstanceId);

    if (error) {
      console.error('Error fetching PC sync status:', error);
      return [];
    }

    // Create a Set of configuration_release_ids that are installed
    const installedReleaseIds = new Set(
      (data || [])
        .filter((status: any) => status.sync_status === 'installed')
        .map((status: any) => status.configuration_release_id)
    );

    // Filter configuration releases to only include installed ones
    const installedReleases = configurationReleases.filter((release) =>
      installedReleaseIds.has(release.id)
    );

    console.log(`PC ${pcInstanceId} has ${installedReleases.length} installed experiences out of ${configurationReleases.length} total`);
    return installedReleases;
  }

  // ============ Deployment Management ============

  async listConfigurations(): Promise<Configuration[]> {
    const { data, error } = await supabase
      .schema('monitor')
      .from('configuration')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching configurations:', error);
      return [];
    }

    return data || [];
  }

  async listExperienceReleases(experienceId?: string): Promise<ExperienceRelease[]> {
    let query = supabase
      .schema('monitor')
      .from('experience_releases')
      .select('*')
      .order('created_at', { ascending: false });

    if (experienceId) {
      query = query.eq('experience_id', experienceId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching experience releases:', error);
      return [];
    }

    return data || [];
  }

  async getAllConfigurationReleases(): Promise<ConfigurationRelease[]> {
    const { data, error } = await supabase
      .schema('monitor')
      .from('configuration_release')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching all configuration releases:', error);
      return [];
    }

    const releases = data || [];
    const experienceIds = [...new Set(releases.map((r: any) => r.experience_id))];
    const experienceReleaseIds = [...new Set(releases.map((r: any) => r.experience_release_id).filter(Boolean))];

    let expMap = new Map();
    if (experienceIds.length > 0) {
      const { data: experiences, error: expError } = await supabase
        .schema('experiences')
        .from('experiences')
        .select('*')
        .in('id', experienceIds);

      if (!expError && experiences) {
        expMap = new Map(experiences.map((e: any) => [e.id, e]));
      }
    }

    let expReleaseMap = new Map();
    if (experienceReleaseIds.length > 0) {
      const { data: experienceReleases, error: expRelError } = await supabase
        .schema('monitor')
        .from('experience_releases')
        .select('*')
        .in('id', experienceReleaseIds);

      if (!expRelError && experienceReleases) {
        expReleaseMap = new Map(experienceReleases.map((er: any) => [er.id, er]));
      }
    }

    return releases.map((release: any) => ({
      ...release,
      experiences: expMap.get(release.experience_id),
      experience_release: expReleaseMap.get(release.experience_release_id),
    }));
  }

  async upsertConfigurationRelease(params: {
    configuration_id: string;
    experience_id: string;
    experience_release_id: string;
    phase: string;
  }): Promise<ConfigurationRelease | null> {
    const { data, error } = await supabase
      .schema('monitor')
      .from('configuration_release')
      .upsert(params, { onConflict: 'phase,experience_id,configuration_id' })
      .select()
      .single();

    if (error) {
      console.error('Error upserting configuration release:', error);
      return null;
    }

    return data;
  }

  async deleteConfigurationRelease(id: string): Promise<boolean> {
    const { error } = await supabase
      .schema('monitor')
      .from('configuration_release')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting configuration release:', error);
      return false;
    }

    return true;
  }

  async setShouldUpdate(pcInstanceIds: string[], shouldUpdate: boolean): Promise<boolean> {
    const { error } = await supabase
      .schema('infra')
      .from('pc_instances')
      .update({ should_update: shouldUpdate })
      .in('id', pcInstanceIds);

    if (error) {
      console.error('Error setting should_update:', error);
      return false;
    }

    return true;
  }

  // Sticky sync pause (swarm build distribution). The operator is the ONLY
  // writer of these columns — the agent reads them (30s refresh + at sync
  // admission) and gets poked via POST /api/sync/pause|resume for immediate
  // effect. Sticky until resumed; the UI shows a loud indicator meanwhile.
  async setSyncPaused(pcInstanceIds: string[], paused: boolean, operatorName?: string): Promise<boolean> {
    if (pcInstanceIds.length === 0) return true;

    const { error } = await supabase
      .schema('infra')
      .from('pc_instances')
      .update({
        sync_paused: paused,
        sync_paused_at: paused ? new Date().toISOString() : null,
        sync_paused_by: paused ? operatorName ?? null : null,
      })
      .in('id', pcInstanceIds);

    if (error) {
      console.error('Error setting sync_paused:', error);
      return false;
    }

    return true;
  }

  // The language a RUN was actually started in. The queue row carries it for a
  // booked visitor, but a walk-in or a test launch has no queue row at all --
  // and the agent does not report the language back in /api/status. It does
  // write it on the run (experiences.experience_runs.language_id, resolved from
  // the launch's short code), and it reports run_id, so the run row is the one
  // place that knows the language of a manually started experience.
  //
  // public.languages is a handful of rows and never changes during a shift, so
  // it is fetched once and kept.
  private languageCodes: Map<string, string> | null = null;

  private async languageCodeMap(): Promise<Map<string, string>> {
    if (this.languageCodes) return this.languageCodes;
    const { data, error } = await supabase.from('languages').select('id, short_name');
    if (error) {
      console.error('Error loading languages:', error);
      return new Map();
    }
    const map = new Map<string, string>();
    for (const row of (data ?? []) as { id: string; short_name: string | null }[]) {
      // Stored uppercase ("NL"); LanguageFlag wants a lowercase 2-letter code.
      if (row.short_name) map.set(row.id, row.short_name.toLowerCase());
    }
    this.languageCodes = map;
    return map;
  }

  /** Lowercase 2-letter code for a run, or null when the run has no language. */
  async getRunLanguage(runId: string): Promise<string | null> {
    const { data, error } = await supabase
      .schema('experiences')
      .from('experience_runs')
      .select('language_id')
      .eq('id', runId)
      .maybeSingle();
    if (error) {
      console.error('Error loading run language:', error);
      return null;
    }
    const languageId = (data as { language_id: string | null } | null)?.language_id;
    if (!languageId) return null;
    return (await this.languageCodeMap()).get(languageId) ?? null;
  }

  // Out of the room lineup. UNLIKE setSyncPaused above this does NOT write a PC
  // row: the state lives entirely in operations.pc_out_of_service (an open row
  // = out of service), written through a SECURITY DEFINER function that also
  // resolves the operator's display name from operations.operators rather than
  // trusting a name sent from here.
  async setOutOfService(
    pcInstanceId: string,
    value:
      | { active: true; kind: OutOfServiceKind; note?: string | null; operatorId: string }
      | { active: false; operatorId: string },
  ): Promise<boolean> {
    const { error } = value.active
      ? await supabase.schema('operations').rpc('fn_pc_out_of_service', {
          p_pc_instance_id: pcInstanceId,
          p_kind: value.kind,
          p_note: value.note?.trim() || null,
          p_operator_id: value.operatorId,
        })
      : await supabase.schema('operations').rpc('fn_pc_in_service', {
          p_pc_instance_id: pcInstanceId,
          p_operator_id: value.operatorId,
        });

    if (error) {
      console.error('Error setting out_of_service:', error);
      return false;
    }

    return true;
  }

  // The OPEN row for each of these PCs — which ones are out of the lineup, why,
  // who parked them and since when. There is no flag on the PC row, so this IS
  // the out-of-service read; the dashboard merges it onto the rows so every
  // tile keeps reading one object instead of learning about two tables.
  async getOpenOutOfService(pcInstanceIds: string[]): Promise<Map<string, PCOutOfService>> {
    const out = new Map<string, PCOutOfService>();
    if (pcInstanceIds.length === 0) return out;

    const { data, error } = await supabase
      .schema('operations')
      .from('pc_out_of_service')
      .select('*')
      .is('ended_at', null)
      .in('pc_instance_id', pcInstanceIds);

    if (error) {
      console.error('Error loading pc_out_of_service:', error);
      return out;
    }
    for (const row of (data ?? []) as PCOutOfService[]) out.set(row.pc_instance_id, row);
    return out;
  }

  /** Every out-of-service period for one PC, newest first — the downtime history. */
  async getOutOfServiceHistory(pcInstanceId: string, limit = 20): Promise<PCOutOfService[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('pc_out_of_service')
      .select('*')
      .eq('pc_instance_id', pcInstanceId)
      .order('started_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error loading out-of-service history:', error);
      return [];
    }
    return (data ?? []) as PCOutOfService[];
  }

  // Diagnostics policy (operations.diagnostic_config). Admin-edited from the
  // Settings page; the agent reads it per room (LoadDiagnosticConfig) to gate its
  // auto-repair ladder. enforcement/auto_run carry a { default, rooms } jsonb so a
  // room can override the fleet default without a release.
  async getDiagnosticsPolicy(): Promise<DiagnosticsPolicy | null> {
    const { data, error } = await supabase
      .schema('operations')
      .from('diagnostic_config')
      .select('key,value');

    if (error) {
      console.error('Error loading diagnostic_config:', error);
      return null;
    }

    const byKey = new Map<string, any>((data ?? []).map((r: any) => [r.key, r.value]));
    const enforcement = byKey.get('enforcement') ?? {};
    const autoRun = byKey.get('auto_run') ?? {};
    return {
      enforcement: {
        default: (enforcement.default as DiagEnforcement) ?? 'report_only',
        rooms: (enforcement.rooms as Record<string, DiagEnforcement>) ?? {},
      },
      autoRun: {
        default: Boolean(autoRun.default ?? false),
        rooms: (autoRun.rooms as Record<string, boolean>) ?? {},
      },
      disabledTests: (byKey.get('disabled_tests') as string[]) ?? [],
      advisoryOverrides: (byKey.get('advisory_overrides') as string[]) ?? [],
    };
  }

  // Set (value) or clear (value === null) a single room's override inside a
  // room-scoped config key. Read-modify-write of the one jsonb row, preserving
  // its `default` and every other room's entry.
  private async setRoomScopedConfig(
    key: 'enforcement' | 'auto_run',
    roomId: string,
    value: DiagEnforcement | boolean | null,
  ): Promise<boolean> {
    const { data, error: readErr } = await supabase
      .schema('operations')
      .from('diagnostic_config')
      .select('value')
      .eq('key', key)
      .single();

    if (readErr) {
      console.error(`Error reading diagnostic_config[${key}]:`, readErr);
      return false;
    }

    const current = (data?.value as any) ?? {};
    const rooms: Record<string, unknown> = { ...(current.rooms ?? {}) };
    if (value === null) {
      delete rooms[roomId];
    } else {
      rooms[roomId] = value;
    }

    const { error: writeErr } = await supabase
      .schema('operations')
      .from('diagnostic_config')
      .update({ value: { default: current.default, rooms }, updated_at: new Date().toISOString() })
      .eq('key', key);

    if (writeErr) {
      console.error(`Error writing diagnostic_config[${key}]:`, writeErr);
      return false;
    }
    return true;
  }

  async setRoomEnforcement(roomId: string, enforcement: DiagEnforcement): Promise<boolean> {
    return this.setRoomScopedConfig('enforcement', roomId, enforcement);
  }

  async setRoomAutoRun(roomId: string, enabled: boolean): Promise<boolean> {
    return this.setRoomScopedConfig('auto_run', roomId, enabled);
  }

  // Distributed Wake-on-LAN: flag PCs to be woken. Online RealityAgent peers in the
  // same network poll this flag and send LAN magic packets until the target boots
  // (then the target clears its own flag). Works remotely — it's a DB write, not a
  // LAN packet.
  //
  // This is ONE HALF of waking a machine, and on its own it does nothing when no
  // agent is up on that network — i.e. every morning. Call wakeMachines() in
  // services/wake.ts instead unless you specifically want only the flag.
  async requestWake(pcInstanceIds: string[]): Promise<boolean> {
    if (pcInstanceIds.length === 0) return true;

    const { error } = await supabase
      .schema('infra')
      .from('pc_instances')
      .update({ wake_requested: true, wake_requested_at: new Date().toISOString() })
      .in('id', pcInstanceIds);

    if (error) {
      console.error('Error requesting wake:', error);
      return false;
    }

    return true;
  }

  // Phase 2 gateway power-on (LSC/Tuya). Returns the pc_instance_ids, among the
  // given PCs, that are configured as out-of-band wake gateways (a row in
  // infra.pc_power_control). The operator reads this with the anon key — device_id
  // is non-secret; the Tuya secret lives only in the wake-gateway edge function.
  async getGatewayPcIds(pcInstanceIds: string[]): Promise<string[]> {
    if (pcInstanceIds.length === 0) return [];

    const { data, error } = await supabase
      .schema('infra')
      .from('pc_power_control')
      .select('pc_instance_id')
      .in('pc_instance_id', pcInstanceIds)
      .eq('enabled', true);

    if (error) {
      console.error('Error fetching power-control gateways:', error);
      return [];
    }

    return (data || []).map((r) => r.pc_instance_id);
  }

  // Power-cycle a gateway PC's smart plug out-of-band (off -> on) via the
  // wake-gateway edge function, so it cold-boots and then wakes the rest of its
  // network over LAN. Used when the whole network is dark and peer-WOL can't run.
  async powerOnGateway(pcInstanceId: string): Promise<{ success: boolean; error?: string }> {
    const { data, error } = await supabase.functions.invoke('wake-gateway', {
      body: { pc_instance_id: pcInstanceId },
    });

    if (error) {
      console.error('Error powering on gateway:', error);
      return { success: false, error: error.message };
    }
    if (data && data.success === false) {
      return { success: false, error: data.error };
    }
    return { success: true };
  }

  async getSyncStatusByPCIds(pcIds: string[]): Promise<PCSyncStatus[]> {
    if (pcIds.length === 0) return [];

    const { data, error } = await supabase
      .schema('infra')
      .from('pc_sync_status')
      .select('*')
      .in('pc_instance_id', pcIds)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching sync status:', error);
      return [];
    }

    return data || [];
  }

  async getRoomsForConfiguration(configurationId: string): Promise<Room[]> {
    const { data, error } = await supabase
      .schema('infra')
      .from('rooms')
      .select('*')
      .eq('configuration_id', configurationId)
      .order('name');

    if (error) {
      console.error('Error fetching rooms for configuration:', error);
      return [];
    }

    return data || [];
  }

  // ============ Multiplayer Sessions (experiences schema) ============

  async createMultiplayerSession(params: {
    experienceId: string;
    roomId: string;
    operatorId?: string;
    serverPcInstanceId: string;
    serverIp: string;
    clientPcInstanceIds: string[];
  }): Promise<string | null> {
    const { data, error } = await supabase
      .schema('experiences')
      .from('multiplayer_sessions')
      .insert({
        experience_id: params.experienceId,
        room_id: params.roomId,
        operator_id: params.operatorId || null,
        server_pc_instance_id: params.serverPcInstanceId,
        server_ip: params.serverIp,
        client_pc_instance_ids: params.clientPcInstanceIds,
        status: 'starting_server',
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error creating multiplayer session:', error);
      return null;
    }

    return data?.id || null;
  }

  async updateMultiplayerSessionStatus(
    sessionId: string,
    status: MultiplayerSessionDbStatus,
    errorMessage?: string
  ): Promise<void> {
    const update: Record<string, any> = { status };

    if (status === 'server_ready') {
      update.server_ready_at = new Date().toISOString();
    } else if (status === 'launching_clients') {
      update.clients_launched_at = new Date().toISOString();
    } else if (status === 'completed' || status === 'failed') {
      update.completed_at = new Date().toISOString();
    }

    if (errorMessage) {
      update.error_message = errorMessage;
    }

    const { error } = await supabase
      .schema('experiences')
      .from('multiplayer_sessions')
      .update(update)
      .eq('id', sessionId);

    if (error) {
      console.error('Error updating multiplayer session status:', error);
    }
  }

  // --- Voice Channel Pool ---

  // Get the Discord guild ID for a room (via room → location → discord_guild_id)
  async getGuildIdForRoom(roomId: string): Promise<string | null> {
    const { data: room } = await supabase
      .schema('infra')
      .from('rooms')
      .select('location')
      .eq('id', roomId)
      .single();

    if (!room?.location) return null;

    const { data: location } = await supabase
      .schema('infra')
      .from('location')
      .select('discord_guild_id')
      .eq('id', room.location)
      .single();

    return location?.discord_guild_id || null;
  }

  // Claim an available voice channel for a session in a room
  async claimVoiceChannel(roomId: string, sessionId: string): Promise<{ discord_channel_id: string; id: string } | null> {
    // Find an available channel
    const { data: channel, error } = await supabase
      .schema('infra')
      .from('room_voice_channels')
      .select('id, discord_channel_id')
      .eq('room_id', roomId)
      .eq('in_use', false)
      .limit(1)
      .single();

    if (error || !channel) {
      console.error('No available voice channels:', error);
      return null;
    }

    // Mark it as in use
    const { error: updateError } = await supabase
      .schema('infra')
      .from('room_voice_channels')
      .update({ in_use: true, session_id: sessionId })
      .eq('id', channel.id);

    if (updateError) {
      console.error('Failed to claim voice channel:', updateError);
      return null;
    }

    return channel;
  }

  // Release a voice channel back to the pool
  async releaseVoiceChannel(channelId: string): Promise<void> {
    const { error } = await supabase
      .schema('infra')
      .from('room_voice_channels')
      .update({ in_use: false, session_id: null })
      .eq('id', channelId);

    if (error) {
      console.error('Failed to release voice channel:', error);
    }
  }

  // ============ Software version management (operations schema) ============

  // Desired versions per room per component (agent / ngr-convai).
  async listRoomSoftwareTargets(): Promise<RoomSoftwareTarget[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('room_software_targets')
      .select('*');

    if (error) {
      console.error('Error fetching room software targets:', error);
      return [];
    }

    return data || [];
  }

  // Set (or clear) the desired version for a room/component. Pass null to mark
  // it unmanaged. Keyed on (room_id, component).
  async upsertRoomSoftwareTarget(
    roomId: string,
    component: SoftwareComponent,
    desiredVersion: string | null,
    updatedBy?: string
  ): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('room_software_targets')
      .upsert(
        {
          room_id: roomId,
          component,
          desired_version: desiredVersion,
          updated_at: new Date().toISOString(),
          updated_by: updatedBy ?? null,
        },
        { onConflict: 'room_id,component' }
      );

    if (error) {
      console.error('Error upserting room software target:', error);
      return false;
    }

    return true;
  }

  // Reported installed version + running state per PC per component.
  async getPCSoftwareStatusByPCIds(pcIds: string[]): Promise<PCSoftwareStatus[]> {
    if (pcIds.length === 0) return [];

    const { data, error } = await supabase
      .schema('operations')
      .from('pc_software_status')
      .select('*')
      .in('pc_instance_id', pcIds);

    if (error) {
      console.error('Error fetching pc software status:', error);
      return [];
    }

    return data || [];
  }

  // ============ Operator device version targeting (operations schema) ============

  // All registered RealityOperator installs (Android/Electron/web).
  async listOperatorDevices(): Promise<OperatorDevice[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('operator_devices')
      .select('*')
      .order('last_seen', { ascending: false });

    if (error) {
      console.error('Error fetching operator devices:', error);
      return [];
    }

    return data || [];
  }

  // Self-report from the running app: upserts identity + reported state keyed on
  // device_id. Only the reported columns are sent, so an operator-set label /
  // desired_version is never clobbered by a heartbeat (PostgREST updates only the
  // provided columns on conflict).
  async reportOperatorDevice(report: {
    deviceId: string;
    platform: OperatorPlatform;
    currentVersion: string;
    roomId?: string | null;
  }): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('operator_devices')
      .upsert(
        {
          device_id: report.deviceId,
          platform: report.platform,
          current_version: report.currentVersion,
          room_id: report.roomId ?? null,
          last_seen: new Date().toISOString(),
        },
        { onConflict: 'device_id' }
      );

    if (error) {
      console.error('Error reporting operator device:', error);
      return false;
    }

    return true;
  }

  // Read a single device's target — used by the app on launch to resolve which
  // version it should be running. Returns null if the device isn't registered yet.
  async getOperatorDevice(deviceId: string): Promise<OperatorDevice | null> {
    const { data, error } = await supabase
      .schema('operations')
      .from('operator_devices')
      .select('*')
      .eq('device_id', deviceId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching operator device:', error);
      return null;
    }

    return data;
  }

  // Operator pins (or clears, pass null) a specific device's desired version.
  async setOperatorDeviceTarget(
    deviceId: string,
    desiredVersion: string | null,
    updatedBy?: string
  ): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('operator_devices')
      .update({ desired_version: desiredVersion, updated_by: updatedBy ?? null })
      .eq('device_id', deviceId);

    if (error) {
      console.error('Error setting operator device target:', error);
      return false;
    }

    return true;
  }

  // Operator renames a device (friendly label).
  async setOperatorDeviceLabel(deviceId: string, label: string | null): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('operator_devices')
      .update({ label })
      .eq('device_id', deviceId);

    if (error) {
      console.error('Error setting operator device label:', error);
      return false;
    }

    return true;
  }

  // Operator removes a stale device row (re-registers itself if it reappears).
  async deleteOperatorDevice(deviceId: string): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('operator_devices')
      .delete()
      .eq('device_id', deviceId);

    if (error) {
      console.error('Error deleting operator device:', error);
      return false;
    }

    return true;
  }

  // ============ Business Streaming settings (operations schema) ============

  // Desired PICO Business Streaming settings per room (partial settings.json).
  async listRoomStreamingSettings(): Promise<RoomStreamingSettings[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('room_streaming_settings')
      .select('*');

    if (error) {
      console.error('Error fetching room streaming settings:', error);
      return [];
    }

    return data || [];
  }

  // Set the desired Business Streaming settings for a room. Keyed on room_id.
  async upsertRoomStreamingSettings(
    roomId: string,
    settings: StreamingSettings,
    updatedBy?: string
  ): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('room_streaming_settings')
      .upsert(
        {
          room_id: roomId,
          settings,
          updated_at: new Date().toISOString(),
          updated_by: updatedBy ?? null,
        },
        { onConflict: 'room_id' }
      );

    if (error) {
      console.error('Error upserting room streaming settings:', error);
      return false;
    }

    return true;
  }

  // Remove a room's Business Streaming settings (back to unmanaged).
  async deleteRoomStreamingSettings(roomId: string): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('room_streaming_settings')
      .delete()
      .eq('room_id', roomId);

    if (error) {
      console.error('Error deleting room streaming settings:', error);
      return false;
    }

    return true;
  }

  // Per-PC Business Streaming settings overrides (deep-merged over the room's).
  async getPCStreamingSettingsByPCIds(pcIds: string[]): Promise<PCStreamingSettings[]> {
    if (pcIds.length === 0) return [];

    const { data, error } = await supabase
      .schema('operations')
      .from('pc_streaming_settings')
      .select('*')
      .in('pc_instance_id', pcIds);

    if (error) {
      console.error('Error fetching pc streaming settings:', error);
      return [];
    }

    return data || [];
  }

  // Set the Business Streaming settings override for a PC. Keyed on pc_instance_id.
  async upsertPCStreamingSettings(
    pcInstanceId: string,
    settings: StreamingSettings,
    updatedBy?: string
  ): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('pc_streaming_settings')
      .upsert(
        {
          pc_instance_id: pcInstanceId,
          settings,
          updated_at: new Date().toISOString(),
          updated_by: updatedBy ?? null,
        },
        { onConflict: 'pc_instance_id' }
      );

    if (error) {
      console.error('Error upserting pc streaming settings:', error);
      return false;
    }

    return true;
  }

  // Remove a PC's Business Streaming override (back to the room settings).
  async deletePCStreamingSettings(pcInstanceId: string): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('pc_streaming_settings')
      .delete()
      .eq('pc_instance_id', pcInstanceId);

    if (error) {
      console.error('Error deleting pc streaming settings:', error);
      return false;
    }

    return true;
  }

  // ============ Experience launch args (operations schema) ============

  // All configured launch-args rows, every experience and scope. Small table;
  // the view filters client-side.
  async listExperienceLaunchArgs(): Promise<ExperienceLaunchArgs[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('experience_launch_args')
      .select('*');

    if (error) {
      console.error('Error fetching experience launch args:', error);
      return [];
    }

    return data || [];
  }

  // Save (args) or clear (args = null) the row for one scope. Upsert is done
  // manually (select → update/insert) because the table's uniqueness is
  // enforced by partial indexes, which PostgREST upsert cannot target.
  async saveExperienceLaunchArgs(
    scope: LaunchArgsScope,
    args: LaunchArgsValues | null,
    updatedBy?: string
  ): Promise<boolean> {
    const table = supabase.schema('operations').from('experience_launch_args');

    let query = table.select('id').eq('experience_id', scope.experienceId);
    query = scope.roomId ? query.eq('room_id', scope.roomId) : query.is('room_id', null);
    query = scope.pcInstanceId ? query.eq('pc_instance_id', scope.pcInstanceId) : query.is('pc_instance_id', null);
    query = scope.experienceReleaseId
      ? query.eq('experience_release_id', scope.experienceReleaseId)
      : query.is('experience_release_id', null);

    const { data: existing, error: selError } = await query.maybeSingle();
    if (selError) {
      console.error('Error looking up experience launch args row:', selError);
      return false;
    }

    if (args === null) {
      if (!existing) return true; // nothing to clear
      const { error } = await supabase
        .schema('operations')
        .from('experience_launch_args')
        .delete()
        .eq('id', existing.id);
      if (error) {
        console.error('Error deleting experience launch args:', error);
        return false;
      }
      return true;
    }

    const row = {
      experience_id: scope.experienceId,
      room_id: scope.roomId ?? null,
      pc_instance_id: scope.pcInstanceId ?? null,
      experience_release_id: scope.experienceReleaseId ?? null,
      args,
      updated_at: new Date().toISOString(),
      updated_by: updatedBy ?? null,
    };

    const { error } = existing
      ? await supabase.schema('operations').from('experience_launch_args').update(row).eq('id', existing.id)
      : await supabase.schema('operations').from('experience_launch_args').insert(row);

    if (error) {
      console.error('Error saving experience launch args:', error);
      return false;
    }

    return true;
  }

  // ============ Agent config overrides (operations schema) ============

  // Every configured agent_config row (global + all rooms + all PCs). Small
  // table; the editor filters client-side by scope.
  async listAgentConfig(): Promise<AgentConfig[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('agent_config')
      .select('*');

    if (error) {
      console.error('Error fetching agent config:', error);
      return [];
    }

    return data || [];
  }

  // Save (config) or clear (config = null) the row for one scope. Upsert is done
  // manually (select → update/insert) because the table's uniqueness is enforced
  // by partial indexes, which PostgREST upsert cannot target — same approach as
  // saveExperienceLaunchArgs.
  async saveAgentConfig(
    scope: AgentConfigScope,
    config: AgentConfigValues | null,
    updatedBy?: string
  ): Promise<boolean> {
    const table = supabase.schema('operations').from('agent_config');

    let query = table.select('id');
    query = scope.roomId ? query.eq('room_id', scope.roomId) : query.is('room_id', null);
    query = scope.pcInstanceId
      ? query.eq('pc_instance_id', scope.pcInstanceId)
      : query.is('pc_instance_id', null);

    const { data: existing, error: selError } = await query.maybeSingle();
    if (selError) {
      console.error('Error looking up agent config row:', selError);
      return false;
    }

    // Clearing, or saving an empty object, removes the row (back to inherit).
    if (config === null || Object.keys(config).length === 0) {
      if (!existing) return true; // nothing to clear
      const { error } = await supabase
        .schema('operations')
        .from('agent_config')
        .delete()
        .eq('id', existing.id);
      if (error) {
        console.error('Error deleting agent config:', error);
        return false;
      }
      return true;
    }

    const row = {
      room_id: scope.roomId ?? null,
      pc_instance_id: scope.pcInstanceId ?? null,
      config,
      updated_at: new Date().toISOString(),
      updated_by: updatedBy ?? null,
    };

    const { error } = existing
      ? await supabase.schema('operations').from('agent_config').update(row).eq('id', existing.id)
      : await supabase.schema('operations').from('agent_config').insert(row);

    if (error) {
      console.error('Error saving agent config:', error);
      return false;
    }

    return true;
  }

  // Toggle the experimental flag (experimental PCs are exempt from room reconciliation).
  async setPCExperimental(pcInstanceId: string, experimental: boolean): Promise<boolean> {
    const { error } = await supabase
      .schema('infra')
      .from('pc_instances')
      .update({ experimental })
      .eq('id', pcInstanceId);

    if (error) {
      console.error('Error setting experimental flag:', error);
      return false;
    }

    return true;
  }

  // ---- PICO headsets (infra.hmd_devices) --------------------------------------
  // Rows are created by the room primary's agent when a headset announces itself.
  // Operators own `label` and `paired_pc_instance_id` (the DESIRED PC); agents
  // write the observed_* / bs_* columns. No DELETE: retire with active=false.

  // Active rows by default; includeRetired adds the "uit vloot" ones so the
  // Headsets picker can bring a retired headset back deliberately.
  async getAllHmdDevices(includeRetired = false): Promise<HmdDevice[]> {
    let q = supabase
      .schema('infra')
      .from('hmd_devices')
      .select('*')
      .order('label', { ascending: true, nullsFirst: false });
    if (!includeRetired) q = q.eq('active', true);
    const { data, error } = await q;

    if (error) {
      console.error('Error fetching hmd devices:', error);
      return [];
    }
    return (data || []) as HmdDevice[];
  }

  // Desired headset assignment for a set of PCs at once (the playspace map
  // labels every headset with its PC even when the socket is held elsewhere).
  async getHmdDevicesForPCs(pcInstanceIds: string[]): Promise<HmdDevice[]> {
    if (pcInstanceIds.length === 0) return [];
    const { data, error } = await supabase
      .schema('infra')
      .from('hmd_devices')
      .select('*')
      .eq('active', true)
      .in('paired_pc_instance_id', pcInstanceIds);

    if (error) {
      console.error('Error fetching hmd devices for pcs:', error);
      return [];
    }
    return (data || []) as HmdDevice[];
  }

  // Room plan for the playspace map: the active playspace polygon (+ obstacles)
  // and the room-wide zones and walls RealityStudio drew for the room
  // (scene_name '__room__'). Null when the room has no plan yet.
  async getRoomPlayspace(roomId: string): Promise<RoomPlayspace | null> {
    const asPolygon = (v: unknown): PlanPoint[] =>
      Array.isArray(v)
        ? v
            .filter((p) => p && typeof p.x === 'number' && typeof p.y === 'number')
            .map((p) => ({ x: p.x, y: p.y }))
        : [];
    const [ps, zones, walls] = await Promise.all([
      supabase.schema('experiences').from('playspaces').select('id, boundary, obstacles')
        .eq('room_id', roomId).eq('is_active', true).limit(1),
      supabase.schema('experiences').from('playspace_zones').select('zone_key, zone')
        .eq('room_id', roomId).eq('scene_name', '__room__'),
      supabase.schema('experiences').from('playspace_walls').select('wall_key, wall')
        .eq('room_id', roomId).eq('scene_name', '__room__'),
    ]);
    if (ps.error) console.error('Error fetching playspace:', ps.error);
    if (zones.error) console.error('Error fetching playspace zones:', zones.error);
    if (walls.error) console.error('Error fetching playspace walls:', walls.error);

    const space = ps.data?.[0];
    const out: RoomPlayspace = {
      id: space?.id ?? null,
      boundary: asPolygon(space?.boundary),
      obstacles: Array.isArray(space?.obstacles) ? space.obstacles.map(asPolygon).filter((o: PlanPoint[]) => o.length >= 3) : [],
      zones: {},
      walls: {},
    };
    for (const z of zones.data || []) {
      const poly = asPolygon(z.zone);
      if (poly.length >= 3) out.zones[z.zone_key] = poly;
    }
    for (const w of walls.data || []) {
      const line = asPolygon(w.wall);
      if (line.length >= 2) out.walls[w.wall_key] = line;
    }
    const empty = out.boundary.length === 0 && Object.keys(out.zones).length === 0 && Object.keys(out.walls).length === 0;
    return empty ? null : out;
  }

  // Headsets assigned to one PC (desired assignment). Used by the PC tile to
  // decide which headset is "its" headset — never inferred from which agent
  // happens to hold a socket (the primary holds sockets for the whole room).
  async getHmdDevicesForPC(pcInstanceId: string): Promise<HmdDevice[]> {
    const { data, error } = await supabase
      .schema('infra')
      .from('hmd_devices')
      .select('*')
      .eq('active', true)
      .eq('paired_pc_instance_id', pcInstanceId);

    if (error) {
      console.error('Error fetching hmd devices for pc:', error);
      return [];
    }
    return (data || []) as HmdDevice[];
  }

  async setHmdLabel(id: string, label: string | null): Promise<boolean> {
    const { error } = await supabase
      .schema('infra')
      .from('hmd_devices')
      .update({ label: label && label.trim() ? label.trim() : null, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      console.error('Error setting hmd label:', error);
      return false;
    }
    return true;
  }

  // Desired PC for a headset. The primary agent only rewrites the headset's
  // BStreaming.json on an explicit Push, so this is safe to change any time.
  async setHmdAssignedPC(id: string, pcInstanceId: string | null): Promise<boolean> {
    const { error } = await supabase
      .schema('infra')
      .from('hmd_devices')
      // Assigning a PC always (re)activates the row: a retired headset that an
      // admin picks in the "Headset toevoegen" picker is back in the fleet.
      .update({ paired_pc_instance_id: pcInstanceId, active: true, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      console.error('Error assigning hmd to pc:', error);
      return false;
    }
    return true;
  }

  async retireHmdDevice(id: string): Promise<boolean> {
    const { error } = await supabase
      .schema('infra')
      .from('hmd_devices')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      console.error('Error retiring hmd device:', error);
      return false;
    }
    return true;
  }

  // Helper: Check if PC is online based on last_heartbeat
  isPCOnline(lastHeartbeat: string | null | undefined): boolean {
    if (!lastHeartbeat) return false;

    const now = new Date();
    const heartbeatTime = new Date(lastHeartbeat);
    const diffSeconds = (now.getTime() - heartbeatTime.getTime()) / 1000;

    // Consider online if heartbeat within last 60 seconds
    return diffSeconds < 60;
  }

  // ============ Incidents (operations schema) ============

  // Direct insert of a manual issue marker into operations.incidents. Used for
  // room-scoped "Mark Issue" marks and as the fallback when a PC's agent is
  // unreachable — the marker is never lost. PC-scoped marks with a reachable
  // agent go through POST /api/incidents instead so the agent can attach
  // run/scene context and a log bundle.
  async insertIncident(row: IncidentInsert): Promise<boolean> {
    const { error } = await supabase
      .schema('operations')
      .from('incidents')
      .insert({
        type: 'manual',
        severity: row.severity,
        source: row.source,
        message: row.message,
        pc_instance_id: row.pc_instance_id ?? null,
        room_id: row.room_id ?? null,
        experience_id: row.experience_id ?? null,
        run_id: row.run_id ?? null,
        metadata: row.metadata ?? {},
      });

    if (error) {
      console.error('Error inserting incident:', error);
      return false;
    }

    return true;
  }

  // Operator verdict on an incident the agent filed (no-VR verify modal):
  // which lever the operator chose and what they saw in the headset. Merged
  // into metadata.operator_verdict so the detector's precision per failure
  // class can be measured later. Never throws; false when the row is gone.
  async setIncidentOperatorVerdict(
    incidentId: string,
    verdict: { verdict: string; saw: string; operator_id?: string | null; pc_name?: string | null; note?: string | null },
  ): Promise<boolean> {
    const { data, error: readErr } = await supabase
      .schema('operations')
      .from('incidents')
      .select('metadata')
      .eq('id', incidentId)
      .maybeSingle();
    if (readErr || !data) {
      console.error('Error reading incident for verdict:', readErr);
      return false;
    }
    const metadata = { ...((data.metadata as Record<string, unknown>) ?? {}) };
    metadata.operator_verdict = { ...verdict, at: new Date().toISOString() };
    const { error } = await supabase
      .schema('operations')
      .from('incidents')
      .update({ metadata })
      .eq('id', incidentId);
    if (error) {
      console.error('Error writing incident verdict:', error);
      return false;
    }
    return true;
  }

  // ============ File drops (operations schema) ============

  // Non-expired drops, newest first. The cleanup cron removes expired rows
  // nightly; the filter hides the ones already past expiry but not yet swept.
  async listFileDrops(): Promise<FileDrop[]> {
    const { data, error } = await supabase
      .schema('operations')
      .from('file_drops')
      .select('*')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error listing file drops:', error);
      return [];
    }
    return (data ?? []) as FileDrop[];
  }

  // Upload a file picked on the operator machine into the drop. Bytes go
  // straight to Storage via a broker-signed URL (file-drop-url edge function);
  // the row is inserted as ready with the signed download URL attached.
  async uploadFileDropFromOperator(file: File, note?: string, operatorName?: string): Promise<FileDrop | null> {
    // Row first so the object key can reuse the drop id (same layout the agent
    // uses: <id>/<sanitized name>).
    const { data: row, error: insertError } = await supabase
      .schema('operations')
      .from('file_drops')
      .insert({
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
        note: note || null,
        source: 'operator',
        source_pc_name: operatorName || null,
        status: 'uploading',
      })
      .select('*')
      .single();

    if (insertError || !row) {
      console.error('Error inserting file drop row:', insertError);
      return null;
    }

    const drop = row as FileDrop;
    const objectPath = `${drop.id}/${file.name.replace(/[^A-Za-z0-9._-]+/g, '_') || 'file'}`;
    try {
      const { data: up, error: upErr } = await supabase.functions.invoke('file-drop-url', {
        body: { action: 'upload', object_path: objectPath },
      });
      if (upErr || !up?.signed_url) throw new Error(upErr?.message ?? 'no signed upload url');

      const putResp = await fetch(up.signed_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putResp.ok) throw new Error(`upload PUT returned ${putResp.status}`);

      const { data: sign, error: signErr } = await supabase.functions.invoke('file-drop-url', {
        body: { action: 'sign', object_path: objectPath },
      });
      if (signErr || !sign?.url) throw new Error(signErr?.message ?? 'no signed download url');

      const { data: updated, error: updateErr } = await supabase
        .schema('operations')
        .from('file_drops')
        .update({ status: 'ready', object_path: objectPath, signed_url: sign.url, size_bytes: file.size })
        .eq('id', drop.id)
        .select('*')
        .single();
      if (updateErr || !updated) throw new Error(updateErr?.message ?? 'failed to mark drop ready');
      return updated as FileDrop;
    } catch (err) {
      console.error('Error uploading file drop:', err);
      await supabase
        .schema('operations')
        .from('file_drops')
        .update({ status: 'error', error: err instanceof Error ? err.message : String(err) })
        .eq('id', drop.id);
      return null;
    }
  }

  // Delete a drop early (Storage object via the broker, then the row).
  async deleteFileDrop(drop: FileDrop): Promise<boolean> {
    if (drop.object_path) {
      const { error } = await supabase.functions.invoke('file-drop-url', {
        body: { action: 'delete', object_path: drop.object_path },
      });
      if (error) {
        console.error('Error deleting file drop object:', error);
        return false;
      }
    }
    const { error: rowError } = await supabase
      .schema('operations')
      .from('file_drops')
      .delete()
      .eq('id', drop.id);
    if (rowError) {
      console.error('Error deleting file drop row:', rowError);
      return false;
    }
    return true;
  }

  // ============ Streaming test runs (telemetry schema) ============

  // Open a labeled test run (the "logbook" row). started_at defaults to now().
  async createTestRun(params: {
    label: string;
    configNotes?: string;
    roomId?: string;
    pcNames: string[];
    experienceId?: string;
    experienceVersion?: string;
    startedBy?: string;
  }): Promise<TestRun | null> {
    const { data, error } = await supabase
      .schema('telemetry')
      .from('test_runs')
      .insert({
        label: params.label,
        config_notes: params.configNotes ?? null,
        room_id: params.roomId ?? null,
        pc_names: params.pcNames,
        experience_id: params.experienceId ?? null,
        experience_version: params.experienceVersion ?? null,
        started_by: params.startedBy ?? null,
      })
      .select('*')
      .single();

    if (error) {
      console.error('Error creating test run:', error);
      return null;
    }
    return data as TestRun;
  }

  // Close a test run by stamping ended_at (only if not already closed).
  async endTestRun(id: string): Promise<boolean> {
    const { error } = await supabase
      .schema('telemetry')
      .from('test_runs')
      .update({ ended_at: new Date().toISOString() })
      .eq('id', id)
      .is('ended_at', null);

    if (error) {
      console.error('Error ending test run:', error);
      return false;
    }
    return true;
  }

  // The currently-open test run for a room (ended_at IS NULL), if any.
  async getActiveTestRun(roomId: string): Promise<TestRun | null> {
    const { data, error } = await supabase
      .schema('telemetry')
      .from('test_runs')
      .select('*')
      .eq('room_id', roomId)
      .is('ended_at', null)
      .order('started_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching active test run:', error);
      return null;
    }
    return (data as TestRun) || null;
  }

  // Recent test runs (for the history list + the dashboard).
  async listRecentTestRuns(roomId?: string, limit = 15): Promise<TestRun[]> {
    let query = supabase
      .schema('telemetry')
      .from('test_runs')
      .select('*')
      .order('started_at', { ascending: false })
      .limit(limit);

    if (roomId) query = query.eq('room_id', roomId);

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching test runs:', error);
      return [];
    }
    return (data as TestRun[]) || [];
  }

  // Per-PC performance aggregates for a run (server-side aggregation via RPC).
  async getTestRunReport(runId: string): Promise<TestRunPcStat[]> {
    const { data, error } = await supabase
      .schema('telemetry')
      .rpc('test_run_report', { p_run_id: runId });

    if (error) {
      console.error('Error fetching test run report:', error);
      return [];
    }
    return (data as TestRunPcStat[]) || [];
  }

  // Per-scene performance aggregates for a run.
  async getTestRunSceneReport(runId: string): Promise<TestRunSceneStat[]> {
    const { data, error } = await supabase
      .schema('telemetry')
      .rpc('test_run_scene_report', { p_run_id: runId });

    if (error) {
      console.error('Error fetching test run scene report:', error);
      return [];
    }
    return (data as TestRunSceneStat[]) || [];
  }

  // Per-version soak verdicts for a run — cycles/completed/crashed from the
  // agent-written experience_runs + experience_crashes rows inside the run's
  // window (requires migration 20260831084513). Empty for pure streaming runs.
  async getTestRunSoakReport(runId: string): Promise<TestRunSoakStat[]> {
    const { data, error } = await supabase
      .schema('telemetry')
      .rpc('test_run_soak_report', { p_run_id: runId });

    if (error) {
      console.error('Error fetching test run soak report:', error);
      return [];
    }
    return (data as TestRunSoakStat[]) || [];
  }

  // Crash detail rows for a soak run's drill-down table.
  async getTestRunSoakCrashes(runId: string): Promise<TestRunSoakCrash[]> {
    const { data, error } = await supabase
      .schema('telemetry')
      .rpc('test_run_soak_crashes', { p_run_id: runId });

    if (error) {
      console.error('Error fetching test run soak crashes:', error);
      return [];
    }
    return (data as TestRunSoakCrash[]) || [];
  }
}

export const databaseService = new DatabaseService();
